<template>
  <div class="ma-0 ml-1 mr-2 document-sync-component" style="display: flex">
    <v-row dense>
      <v-col cols="12" md="4">
        <v-card outlined tile class="rounded-xl" style="height: 95%">
          <v-overlay :value="loadingCheckTakeControl" absolute>
            <div class="text-center">
              <div>
                <v-progress-circular
                  class="my-4"
                  :size="50"
                  width="5"
                  color="primary"
                  indeterminate
                ></v-progress-circular>
              </div>
              <p class="display-1">{{ $t("CheckingControl") }}...</p>
            </div>
          </v-overlay>
          <div>
            <div class="d-flex align-center">
              <h4 class="pa-4 pr-0 pb-2 truncate flex" style="max-width: 50%">
                {{ $t("DocumentProcessing") }}
              </h4>
              <document-counter-component
                class="mt-1 mr-2"
                :refresh="refreshCounter"
              ></document-counter-component>
            </div>
            <div class="d-flex align-center ml-4 mr-4 mb-2 grey--text caption">
              {{ $t("LastUpdated") }}: {{ lastUpdated }}
            </div>
            <span class="d-flex ml-4 mr-4 mb-2 caption" v-if="connectionStatus"
                  :class="{
                      'grey--text lighten-1': (connectionStatus.type === 'info' || connectionStatus.type === 'success'),
                      'amber--text lighten-1': connectionStatus.type === 'warning',
                      'red--text lighten-1': connectionStatus.type === 'error',
                    }">
              {{ connectionStatus.message }}
            </span>
            <v-row dense class="mx-2 mb-2 pb-1">
              <v-col cols="12" class="py-1 px-2">
                <v-btn
                  small
                  color="primary"
                  @click="forceAutoImport"
                  :loading="isLoadingForceAutoimport"
                  :disabled="isLoadingForceAutoimport"
                  class="col"
                >
                  <template v-slot:loader>
                    <span class="custom-loader">
                      <v-icon light>mdi-cached</v-icon>
                    </span>
                  </template>
                  <v-icon class="me-2">mdi mdi-sync-circle</v-icon>
                  {{ $t("ForceSynchronization") }}
                </v-btn>
              </v-col>
              <v-col cols="12" class="py-0">
                <v-row class="ma-0">
                <v-btn
                  style="width: 100%;"
                  class="col-6 ma-1"
                  small
                  color="red accent-4"
                  @click="deleteAllFailedDocument"
                >
                  {{ $t("DeleteAllFailedDocuments") }}
                </v-btn>
                <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    v-bind="attrs"
                    v-on="on"
                    style="width: 100%;"
                    class="col ma-1"
                    small
                    color="orange accent-4"
                    @click="retryAllFailedDocuments"
                  >
                    {{ $t("Retry") }}
                  </v-btn>
                </template>
                <span>{{ $t("RetryAllFailed") }}</span>
              </v-tooltip>
              </v-row>
              </v-col>
            </v-row>
          </div>
          <v-row justify="center" align="center" v-if="loading">
            <div class="text-center">
              <div>
                <v-progress-circular
                  class="my-4"
                  :size="50"
                  width="5"
                  color="primary"
                  indeterminate
                ></v-progress-circular>
              </div>
              <p class="display-1">{{ $t("Loading") }}...</p>
            </div>
          </v-row>
          <div style="max-height: calc(75vh - 100px); overflow-y: auto; overflow-x: hidden;">
            <v-container class="pa-0">
              <v-simple-table dense height="calc(75vh - 100px)" v-if="!loading">
                <tbody>

                  <DocumentRecognitionItem
                    v-for="(item, index) in synchronizedDocuments"
                    :key="index"
                    :index="index"
                    @on-document-click="handleDocumentSelection"
                    @on-document-deleted="documentHasBeenDeleted"
                    :document-item="item"
                    :class="{
                        'selected-row':
                          selectedDocument && selectedDocument.messageId === item.messageId && (item.status === recognitionStatus.Failed || item.status === recognitionStatus.PendingReview),
                        'cursor-pointer':
                          item.status === recognitionStatus.Failed || item.status === recognitionStatus.PendingReview,
                        'no-hover':
                          item.status !== recognitionStatus.Failed && item.status !== recognitionStatus.PendingReview,
                      }"
                  >
                  </DocumentRecognitionItem>
                </tbody>
              </v-simple-table>
            </v-container>
          </div>
        </v-card>
      </v-col>
      <v-col cols="12" md="4">
        <v-card outlined tile>
          <PdfViewer
            v-show="showViewer"
            :ref="viewerKey"
          />
        </v-card>
      </v-col>
      <v-col cols="12" md="4">
        <DocumentPendingReviewSection :selected-review-item="selectedDocument"
                                      @user-began-editing-a-document="setControlTakenOptionForMessage"
                                      @changes-have-been-saved="changesHaveBeenSaved"
                                      @document-edited-without-save="changeDocumentEditedWithoutSave" >
        </DocumentPendingReviewSection>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { required, requiredIf } from "vuelidate/lib/validators";
import { validationMixin } from "vuelidate";
import DocumentViewer from "@/components/search/results/DocumentViewer";
import PdfViewer from "@/components/document/PdfViewer";
import DocumentIntelligenceProvidersService from "@/services/DocumentIntelligenceProvidersService";
import AttachmentList from "@/components/document/documentModal/attachment/AttachmentList";
import Utils from "@/ecm/common/Utils";
import DocumentService from "@/services/DocumentService";
import DocumentField from "@/components/document/documentModal/field/DocumentField";
import { EventBus } from "@/eventBus.js";
import RecognitionStatus from "@/ecm/variables/RecognitionStatus.js";
import "splitpanes/dist/splitpanes.css";
import AutoImportService from "../../services/AutoImportService";
import DocumentRecognitionItem from "@/components/aiRecognition/Components/DocumentRecognitionItem.vue";
import DocumentCounterComponent from "@/components/aiRecognition/Components/DocumentCounterComponent.vue";
import DocumentPendingReviewSection from "@/components/aiRecognition/Components/DocumentPendingReviewSection.vue";

export default {
  async beforeRouteLeave(to, from, next) {   
    if (to.fullPath !== '/login' && this.documentEditedWithoutSave === true) {
      try {
        const res = await this.unsavedChangesMessageConfirm();
        switch( res ) {
          case 2:
            return;
          case 1:
            this.loading = true;
            await DocumentIntelligenceProvidersService.deleteLockedDocument(this.selectedDocument.messageId)
            this.loading = false;
            next();
            break;
        }
      } catch (error) {
        console.error("Error during save:", error);
        next(false);
      }
    } else {
      next();
    }
  },
  data() {
    return {
      connection: null,
      connectionStatus: null,
      signalRIsTryingToReconnect: false,
      synchronizedDocuments: [],
      selectedDocument: null,
      documentViewerKey: "DocumentViewer",
      documentListKey: "DocumentList",
      viewerKey: "documentListPdfViewer",
      recognitionStatus: RecognitionStatus,
      showViewer: false,
      currentUser: this.$store.getters["account/user"],
      apiUrl: this.$store.getters["account/apiUrl"],
      token: this.$store.getters["account/token"],
      lastUpdated: "",
      loading: false,
      isLoadingForceAutoimport: false,
      selectedPreviousDocumentIndex: -1,
      documentEditedWithoutSave: false,
      loadingCheckTakeControl: false,
      refreshCounter: 0
    };
  },
  mixins: [validationMixin],
  validations: {
    contentTypeId: { required },
    document: {
      contentType: { required },
      fields: {
        $each: {
          value: {
            required: requiredIf(function (model) {
              return model.isRequired;
            }),
          },
        },
      },
    },
  },
  components: {
    DocumentRecognitionItem,
    DocumentPendingReviewSection,
    DocumentViewer,
    PdfViewer,
    AttachmentList,
    DocumentField,
    RecognitionStatus,
    DocumentCounterComponent,
  },
  props: {
    filePath: String,
  },
  computed: {
    viewerInstance() {
      return this.$refs[this.viewerKey];
    }
  },
  methods: {
    manageSignalR() {
      this.connection = $.hubConnection(this.apiUrl);
      this.connection.qs = { access_token: this.token };
      let notificationHub = this.connection.createHubProxy("notificationHub");
      let self = this;

      notificationHub.on("refresh-queues", (refreshedDocument) => {
        self.setUpdatedObject(refreshedDocument);
        this.updateLastUpdated();
      });

      notificationHub.on("user-took-control-of-document", (takeControlResponse) => {
        if (takeControlResponse.PreviousDocumentTakenByCurrentUser) {
          let objLostControl = self.synchronizedDocuments.find(x => x.messageId === takeControlResponse.PreviousDocumentTakenByCurrentUser);

          if (objLostControl)
            objLostControl.controlTakenByUserID = null;
        }

        if (takeControlResponse.UserID !== this.currentUser.Id) {
          self.removeSelectedDocument(takeControlResponse.MessageID);
          this.documentEditedWithoutSave = false;
        }

        let obj = self.synchronizedDocuments.find(x => x.messageId === takeControlResponse.MessageID);

        this.setControlTakenOptionForMessage(obj, takeControlResponse.UserID);
        this.updateLastUpdated();
      });

      notificationHub.on("user-lost-control-of-document", (lostControlResponse) =>
      {
        let obj = self.synchronizedDocuments.find(x => x.messageId === lostControlResponse);
        obj.controlTakenByUserID = null;
        this.updateLastUpdated();
      });

      EventBus.$on("connection-status", (message) => {
        this.connectionStatus = message;
      });

      // Handle SignalR events for connection status
      this.connection.connectionSlow(() => {
        EventBus.$emit('connection-status', { message: self.$t("SignalRInSlowMode"), type: "warning" });
      });

      this.connection.reconnecting(() => {
        EventBus.$emit('connection-status', { message: self.$t("SignalRTryingToReconnect"), type: "warning" });
      });

      this.connection.reconnected(() => {
        EventBus.$emit('connection-status', null);
      });

      this.connection.disconnected(() => {
        EventBus.$emit('connection-status', { message: self.$t("SignalRDisconnected"), type: "error" });
        // Attempt to reconnect
        self.tryReconnect();
      });

      this.connection.error((error) => {
        EventBus.$emit('connection-status', { message: self.$t("SignalRError"), type: "error" });
      });

      this.connection.start().done(() => {
      }).fail((error) => {
        EventBus.$emit('connection-status', { message: self.$t("SignalRCouldNotReconnect"), type: "error" });
      });
    },

    changesHaveBeenSaved(messageId) {
      this.synchronizedDocuments = this.synchronizedDocuments.filter((doc) => doc.messageId !== messageId);

      this.documentEditedWithoutSave = false;
      this.updateRefreshCounter();
      this.openFirstDocument();
    },

    changeDocumentEditedWithoutSave(newValue) {
      this.documentEditedWithoutSave = newValue;
    },

    documentHasBeenDeleted(messageId) {
      this.documentEditedWithoutSave = false;

      this.synchronizedDocuments = this.synchronizedDocuments.filter((doc) => doc.messageId !== messageId);

      this.openFirstDocument();
      this.updateRefreshCounter();
    },

    tryReconnect() {
      if (this.signalRIsTryingToReconnect)
        return;

      let attempts = 0;
      let maxAttempts = 5;
      let self = this;
      this.signalRIsTryingToReconnect = true;

      function reconnect() {
        if (attempts < maxAttempts) {
          self.connection.start().done(() => {
            self.signalRIsTryingToReconnect = false;
            EventBus.$emit('connection-status', null);
          }).fail(() => {
            attempts++;
            setTimeout(reconnect, 5000); // Retry after 5 seconds
          });
        } else {
          EventBus.$emit('connection-status', { message: self.$t("SignalRCouldNotReconnect"), type: "error" });
        }
      }

      reconnect();
    },

    deleteAllFailedDocument() {
      this.$confirm(this.$t("DeleteAllFailedDocumentsExceptBlocked"), {
        title: this.$t("DeleteAllFailedDocuments"),
        icon: "mdi-warning",
        color: "orange",
        buttonTrueText: this.$t("Ok"),
        buttonFalseText: this.$t("Cancel"),
      }).then((res) => {
        if (res === 1) {
          DocumentIntelligenceProvidersService.removeAllFailedDocuments().then(
            (response) => {
              if (response) {
                Utils.toast.success(this.$t("DocumentsDeletedSuccessfully"));

                this.synchronizedDocuments = [...this.synchronizedDocuments.filter(x => x.status !== RecognitionStatus.Failed)];
                this.updateRefreshCounter();
                this.openFirstDocument();
              }
              else {
                Utils.toast.error(this.$t("FailedToDeleteDocuments"));
              }
            }
          );
        }
      });
    },

    openFirstDocument() {
      this.documentEditedWithoutSave = false;
      this.selectedDocument = null;
      this.showViewer = false;

      for (let i = 0; i < this.synchronizedDocuments.length; i++) {
        const item = this.synchronizedDocuments[i];
        if (item.status === RecognitionStatus.PendingReview || item.status === RecognitionStatus.Failed) {
          this.handleDocumentSelection(item, i);
          break;
        }
      }
    },

    async unsavedChangesMessageConfirm() {
      return new Promise((resolve) => {
        this.$confirm(this.$t("UnsavedChanges"), {
          title: this.$t("PendingChanges"),
          icon: "mdi-warning",
          color: "orange",
          width: 400,
          buttonTrueText: this.$t("DiscardChanges"),
          buttonTrueColor: "grey",
          buttonFalseText: this.$t("Cancel"),
        }).then((res) => {
          resolve(res);
        })
      })
    },

    async handleDocumentSelection(document, index) {
      //Only allow to open the document if the status is pending review or failed
      if (document.status !== RecognitionStatus.PendingReview && document.status !== RecognitionStatus.Failed) {
         return;
      }
      if (this.documentEditedWithoutSave && this.selectedPreviousDocumentIndex !== index) {

        // Check that the control is not taken before you are asked if you want to discard the changes.
        this.loadingCheckTakeControl = true;
        const result = await DocumentIntelligenceProvidersService.checkDocumentAvailability(this.selectedDocument.messageId)
        this.loadingCheckTakeControl = false;

        // The document is already being edited
        if (this.selectedDocument.messageId === document.messageId) {
          return;
        }

        // Nobody is editing, or another user.
        if (!result || result.UserID !== this.currentUser.Id) {
          this.loadDocumentFields(document);
          return;
        }

        let optionResult = await this.unsavedChangesMessageConfirm();

        switch (optionResult) {
          case 2:
            return;
          case 1:
            this.loadingCheckTakeControl = true;
            DocumentIntelligenceProvidersService.deleteLockedDocument(this.selectedDocument.messageId)
              .then(() => {
                this.loadingCheckTakeControl = false;
                this.setControlTakenOptionForMessage(this.selectedDocument, null);
                this.loadDocumentFields(document);
                this.documentEditedWithoutSave = false;
              })
              .catch(() => {
                this.loadingCheckTakeControl = false;
              });
            break;
        }

      }
      else {
        this.loadDocumentFields(document);
      }
    },

    loadDocumentFields(document) {
      this.selectedDocument = document;
      this.documentEditedWithoutSave = false;

      this.loadFile(document);
    },

    setControlTakenOptionForMessage(document, userId) {
      this.synchronizedDocuments.forEach(doc => {
        if (doc.controlTakenByUserID && doc.controlTakenByUserID === userId && doc.messageId !== document.messageId)
          doc.controlTakenByUserID = null;
      });

      document.controlTakenByUserID = userId == null ? null : userId;
    },

    loadFile(item) {
      let fileUrl = encodeURIComponent(
        DocumentService.displayTempPdf(
          item.messageId,
          item.filePath.split(".").pop(),
          "PendingObjects"
        )
      );
      this.viewerInstance.load(fileUrl);
      this.showViewer = true;
    },

    getFormattedDate(importedDate) {
      return Utils.date.toUserFormatFromIso(importedDate);
    },

    getQueues() {
      this.loading = true;

      DocumentIntelligenceProvidersService.getQueuesMessages().then(
        (response) => {
          this.synchronizedDocuments = response.sort(
            (a, b) => new Date(b.importDate) - new Date(a.importDate)
          );
          this.updateLastUpdated();
          this.getAllLockedDocuments();
          this.openFirstDocument();
          this.loading = false;
        }
      );
    },

    updateOrAddDocument(pendingObject) {
      const existingIndex = this.synchronizedDocuments.findIndex((doc) => doc.messageId === pendingObject.messageId);

      if (existingIndex !== -1) {
        this.synchronizedDocuments[existingIndex] = pendingObject;
      } else {
        this.synchronizedDocuments.push(pendingObject);
      }
    },

    setUpdatedObject(pendingObject) {
      switch (pendingObject.status) {
        case RecognitionStatus.PendingRecognizing:
        case RecognitionStatus.OcrProcess:
        case RecognitionStatus.InRecognitionQueue:
        case RecognitionStatus.BeingRecognised:
        case RecognitionStatus.Recognized:
        case RecognitionStatus.NotRecognized:
        case RecognitionStatus.Failed:
        case RecognitionStatus.PendingReview:
          this.updateOrAddDocument(pendingObject);
          break;
        case RecognitionStatus.Deleted:
          this.documentHasBeenDeleted(pendingObject.messageId);
          break;
        case RecognitionStatus.Validated:
          setTimeout(() => {
            this.synchronizedDocuments = this.synchronizedDocuments.filter((doc) => doc.messageId !== pendingObject.messageId);
          }, 5000);
          break;
      }

      this.synchronizedDocuments = this.synchronizedDocuments
        .sort((a, b) => new Date(b.importDate) - new Date(a.importDate));

      this.updateRefreshCounter();

      if (this.selectedDocument) {
          const updatedDocument = this.synchronizedDocuments.find(
              (doc) => doc.messageId === this.selectedDocument.messageId
          );

          if (updatedDocument) {
              this.selectedDocument = updatedDocument;
          } else {
              this.selectedDocument = this.synchronizedDocuments.length > 0
                  ? this.synchronizedDocuments[0]
                  : null;
          }
      }

      this.getAllLockedDocuments();
    },
    updateLastUpdated() {
      const nowIso = new Date().toISOString();
      this.lastUpdated = this.getFormattedDate(nowIso);
    },
    forceAutoImport() {
      if (this.isLoadingForceAutoimport) return;

      this.isLoadingForceAutoimport = true;

      AutoImportService.forceAutoImportJob()
        .then(async (response) => {
          Utils.toast.success(this.$t("DocSynchronizationSuccess"));
          await this.getAllLockedDocuments();
        })
        .catch((error) => {
          Utils.toast.error(this.$t("DocSynchronizationError"));
        })
        .finally(() => {
          this.isLoadingForceAutoimport = false;
        });
    },
    getAllLockedDocuments() {
      DocumentIntelligenceProvidersService.getAllAutoImportDocumentsLocked().then((response) => {
        this.synchronizedDocuments.forEach(doc => {
          const lockedDocument = response.find(lockedDoc => lockedDoc.MessageID === doc.messageId);

          if (lockedDocument) {
            this.setControlTakenOptionForMessage(doc, lockedDocument.UserID);
          }
        });
      });
    },
    removeSelectedDocument(messageId) {
      if (this.selectedDocument && this.selectedDocument.messageId === messageId) {
        this.selectedDocument = null;
        this.showViewer = false;
      }
    },
    retryAllFailedDocuments() {
      let failedDocuments = this.synchronizedDocuments.filter(x => x.status === RecognitionStatus.Failed);
      if (failedDocuments.length !== 0) {
        let retryFailedRequest = {
          PendingObjects : failedDocuments
        }

        DocumentIntelligenceProvidersService.sendPendingFailedObjectsToRequeue(retryFailedRequest).then((response) => {
          if (response && response > 0) {
            Utils.toast.success(response + " " + this.$t("DocumentsSuccesfullyRequeued"));
            this.updateLastUpdated();
            this.updateRefreshCounter();
          } else {
            Utils.toast.error(this.$t("ErrorRetryingDocuments"));
          }
        })
      }
    },
    updateRefreshCounter() {
      let today = new Date();
      today.setMilliseconds(0);
      this.refreshCounter = today.getTime();
    }
  },
  mounted() {
    this.updateRefreshCounter();
    this.getQueues();
    this.manageSignalR();
  },
  destroyed() {
    if (this.connection) {
      $(this.connection).unbind("onReconnecting", null);
      $(this.connection).unbind("onReconnect", null);
      $(this.connection).unbind("onDisconnect", null);
      $(this.connection).unbind("onError", null);
      $(this.connection).unbind("onConnectionSlow", null);
      this.connection.stop();

      this.connection = null;
    }
  }
};
</script>
<style>

.document-sync-component .no-hover:hover {
  background: transparent !important;
}
.document-sync-component .loading-icon {
  animation: spin 5s linear infinite;
  font-size: small !important;
}
.document-sync-component tbody tr.selected-row {
  background-color: #d1cece83 !important;
}
.document-sync-component .cursor-pointer {
  cursor: pointer;
}
.document-sync-component .provider-width {
  max-width: 7vw;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.document-sync-component .fileName-width {
  max-width: 16vw;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.document-sync-component .truncate {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: inline-block;
  max-width: 100%;
}
.custom-loader {
  animation: loader 1s infinite;
  display: flex;
}
.document-sync-component .triangle-container {
  display: flex;
  justify-content: center;
  align-items: center;
}

.document-sync-component .triangle {
  position: absolute;
  top: 0;
  left: 0;
  width: 0;
  height: 0;
  border-top: 2.3rem solid ;
  border-right: 2.3rem solid transparent;
}

.document-sync-component .icon-container {
  position: absolute;
  top: -2.4rem;
  right: -1.1rem;
}
@-moz-keyframes loader {
  from {
    transform: rotate(360deg);
  }
  to {
    transform: rotate(0);
  }
}
@-webkit-keyframes loader {
  from {
    transform: rotate(360deg);
  }
  to {
    transform: rotate(0);
  }
}
@-o-keyframes loader {
  from {
    transform: rotate(360deg);
  }
  to {
    transform: rotate(0);
  }
}
@keyframes loader {
  from {
    transform: rotate(360deg);
  }
  to {
    transform: rotate(0);
  }
}
</style>
